import {
  Page,
  EmptyRow,
  usePromise,
  getLinkedAccounts,
} from "@keycloak/keycloak-account-ui";
import type { LinkedAccountRepresentation } from "@keycloak/keycloak-account-ui";
import { useEnvironment } from "@keycloak/keycloak-account-ui";
import { DataList, Stack, StackItem, Title } from "@patternfly/react-core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGroupsService } from "../groups-service/GroupsServiceContext";
import { LinkedAccountsToolbar } from "./LinkedAccountsToolbar";
import { AccountRow } from "./AccountRow";
import { AccountEnvironmentExtended } from "../environment";

type PaginationParams = {
  first: number;
  max: number;
};
type LinkedAccountQueryParams = PaginationParams & {
  search?: string;
  linked?: boolean;
};
export const LinkedAccounts = () => {
  const { t } = useTranslation();
  const groupsService = useGroupsService();
  const context = useEnvironment<AccountEnvironmentExtended>();
  const [linkedAccounts, setLinkedAccounts] = useState<
    LinkedAccountRepresentation[]
  >([]);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [unlinkedAccounts, setUnlinkedAccounts] = useState<
    LinkedAccountRepresentation[]
  >([]);

  // ✅ Typed params include `search?: string`
  const [paramsLinked, setParamsLinked] = useState<LinkedAccountQueryParams>({
    first: 0,
    max: 11,
    linked: true,
    search: "",
  });

  useEffect(() => {
    setUserRoles(groupsService.getUserRoles());
  }, []);

  const [paramsUnlinked, setParamsUnlinked] =
    useState<LinkedAccountQueryParams>({
      first: 0,
      max: 11,
      linked: false,
      search: "",
    });

  const [key, setKey] = useState(0);
  const refresh = () => setKey((k) => k + 1);

  usePromise(
    (signal) => getLinkedAccounts({ signal, context }, paramsUnlinked),
    setUnlinkedAccounts,
    [paramsUnlinked, key],
  );

  usePromise(
    (signal) => getLinkedAccounts({ signal, context }, paramsLinked),
    setLinkedAccounts,
    [paramsLinked, key],
  );
  

  const canManageLinks = userRoles.includes("manage-account-links");

  return (
    <Page
      title={t("linkedAccounts")}
      description={t("linkedAccountsIntroMessage")}
    >
      <Stack hasGutter>
        <StackItem>
          <Title headingLevel="h2" className="pf-v5-u-mb-lg" size="xl">
            {t("linkedLoginProviders")}
          </Title>

          <LinkedAccountsToolbar
            onFilter={(search) =>
              setParamsLinked({ ...paramsLinked, first: 0, search })
            }
            count={linkedAccounts.length}
            first={paramsLinked.first}
            max={paramsLinked.max}
            onNextClick={() =>
              setParamsLinked({
                ...paramsLinked,
                first: paramsLinked.first + paramsLinked.max - 1,
              })
            }
            onPreviousClick={() =>
              setParamsLinked({
                ...paramsLinked,
                first: paramsLinked.first - paramsLinked.max + 1,
              })
            }
            // IMPORTANT: toolbar currently calls onPerPageSelect(first, max)
            onPerPageSelect={(first, max) =>
              setParamsLinked({ ...paramsLinked, first, max })
            }
            hasNext={linkedAccounts.length > paramsLinked.max - 1}
          />

          <DataList id="linked-idps" aria-label={t("linkedLoginProviders")}>
            {linkedAccounts.length > 0 ? (
              linkedAccounts.map(
                (account, index) =>
                  index !== paramsLinked.max - 1 && (
                    <AccountRow
                      key={account.providerName}
                      account={account}
                      isLinked
                      refresh={refresh}
                    />
                  ),
              )
            ) : (
              <EmptyRow message={t("linkedEmpty")} />
            )}
          </DataList>
        </StackItem>

        {canManageLinks && (
          <StackItem>
            <Title
              headingLevel="h2"
              className="pf-v5-u-mt-xl pf-v5-u-mb-lg"
              size="xl"
            >
              {t("unlinkedLoginProviders")}
            </Title>

            <LinkedAccountsToolbar
              onFilter={(search) =>
                setParamsUnlinked({ ...paramsUnlinked, first: 0, search })
              }
              count={unlinkedAccounts.length}
              first={paramsUnlinked.first}
              max={paramsUnlinked.max}
              onNextClick={() =>
                setParamsUnlinked({
                  ...paramsUnlinked,
                  first: paramsUnlinked.first + paramsUnlinked.max - 1,
                })
              }
              onPreviousClick={() =>
                setParamsUnlinked({
                  ...paramsUnlinked,
                  first: paramsUnlinked.first - paramsUnlinked.max + 1,
                })
              }
              onPerPageSelect={(first, max) =>
                setParamsUnlinked({ ...paramsUnlinked, first, max })
              }
              hasNext={unlinkedAccounts.length > paramsUnlinked.max - 1}
            />

            <DataList
              id="unlinked-idps"
              aria-label={t("unlinkedLoginProviders")}
            >
              {unlinkedAccounts.length > 0 ? (
                unlinkedAccounts.map(
                  (account, index) =>
                    index !== paramsUnlinked.max - 1 && (
                      <AccountRow
                        key={account.providerName}
                        account={account}
                        refresh={refresh}
                      />
                    ),
                )
              ) : (
                <EmptyRow message={t("unlinkedEmpty")} />
              )}
            </DataList>
          </StackItem>
        )}
      </Stack>
    </Page>
  );
};

export default LinkedAccounts;
